import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
} from 'prosci-ui';
import { Check } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import type { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { useHubAppCopy } from '../../hooks';
import { getAnnualPriceData, formatAnnualPrice, CurrencyCode } from '../../utils/currency';

interface TierPricing {
  tier: string;
  currency: string;
  amount: number;
  stripe_price_id: string;
  pricing_tier: number;
  requires_certification: boolean;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface InvoiceBillingData {
  customerName: string;
  organizationName?: string;
  phone?: string;
  poNumber?: string;
  billingAddress: BillingAddress;
  vatId?: string;
}

interface InvoiceRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (billingData: InvoiceBillingData) => void;
  loading?: boolean;
  selectedTier: string;
  tierPricing: TierPricing | null;
  currency: string;
  daysUntilDue: number;
  userEmail: string;
  defaultCustomerName?: string;
  defaultOrganizationName?: string;
  stripePublishableKey: string;
  vatCollectionEnabled?: boolean;
  googleMapsApiKey?: string;
  serverError?: string;
  vatError?: string;
  /** Whether the early-adopter discount applies to this invoice (granted-member conversion) */
  discountEligible?: boolean;
  /** True while the discount figures are being fetched — show a price placeholder to avoid a flash */
  discountLoading?: boolean;
  /** Discount percentage to display (e.g. 30) — sourced from proration_preview, never hardcoded */
  discountPercentage?: number;
  /** Currency for the discount figures (proration_preview currency; may differ from `currency`) */
  discountCurrency?: string;
  /** Undiscounted first-year price in cents (proration_preview tier_price_cents) */
  originalAmountCents?: number | null;
  /** Discounted first-year price in cents (proration_preview first_payment_cents) */
  discountedAmountCents?: number | null;
  /** Amount saved in cents (proration_preview discount_cents) */
  savingsAmountCents?: number | null;
}

const PLAN_NAMES: Record<string, string> = {
  'A': 'Starter',
  'B': 'Essentials',
  'C': 'Elevate',
};

export const InvoiceRequestModal: React.FC<InvoiceRequestModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  selectedTier,
  tierPricing,
  currency,
  daysUntilDue,
  userEmail,
  defaultCustomerName = '',
  defaultOrganizationName = '',
  stripePublishableKey,
  vatCollectionEnabled = false,
  googleMapsApiKey,
  serverError,
  vatError,
  discountEligible = false,
  discountLoading = false,
  discountPercentage,
  discountCurrency,
  originalAmountCents = null,
  discountedAmountCents = null,
  savingsAmountCents = null,
}) => {
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.subscriptionCopy?.invoice;

  // Memoize stripe instance to prevent re-creation on each render
  const stripePromise = useMemo(
    () => stripePublishableKey ? loadStripe(stripePublishableKey) : null,
    [stripePublishableKey]
  );

  // Form state
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [billingAddress, setBillingAddress] = useState<BillingAddress | null>(null);
  const [organizationName, setOrganizationName] = useState<string | undefined>(undefined);
  const [addressComplete, setAddressComplete] = useState(false);
  const [vatId, setVatId] = useState('');
  const [isBusinessPurchase, setIsBusinessPurchase] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens, pre-fill with defaults
  useEffect(() => {
    if (isOpen) {
      // Leave customer name blank if it's the placeholder "Unknown Unknown"
      const customerNameToUse = defaultCustomerName === 'Unknown Unknown' ? '' : defaultCustomerName;
      setCustomerName(customerNameToUse);
      setPhone('');
      setPoNumber('');
      setBillingAddress(null);
      setOrganizationName(undefined);
      setAddressComplete(false);
      setVatId('');
      setIsBusinessPurchase(false);
      setErrors({});
    }
  }, [isOpen, defaultCustomerName]);

  const planName = copy?.planNames?.[selectedTier] || PLAN_NAMES[selectedTier] || selectedTier;

  const formattedPrice = tierPricing
    ? formatAnnualPrice(tierPricing.amount, currency as CurrencyCode)
    : 'Loading...';

  // Early-adopter discount display (granted-member invoice conversions).
  // All three figures (original, discounted, savings) come from the same
  // proration_preview response, in proration_preview's own currency, so the
  // numbers shown here match exactly what the invoice will charge and
  // original − savings = discounted always holds. Nothing is computed locally.
  const discountCurrencyCode = (discountCurrency || currency) as CurrencyCode;

  const formattedDiscountedPrice =
    discountEligible && discountedAmountCents != null
      ? formatAnnualPrice(discountedAmountCents, discountCurrencyCode)
      : null;

  const showDiscount = discountEligible && !!formattedDiscountedPrice;

  // Struck-through original: prefer proration_preview's tier_price_cents so it
  // stays consistent with the discounted figure; fall back to the pricing
  // endpoint amount only when we're not rendering a discount.
  const formattedOriginalPrice =
    showDiscount && originalAmountCents != null
      ? formatAnnualPrice(originalAmountCents, discountCurrencyCode)
      : formattedPrice;

  const savingsPriceData =
    showDiscount && savingsAmountCents != null
      ? getAnnualPriceData(savingsAmountCents, discountCurrencyCode)
      : null;
  // Savings is a one-time first-year amount, so drop the "/yr" suffix and join
  // the currency suffix with a space (matching formatAnnualPrice).
  const formattedSavings = savingsPriceData
    ? `${savingsPriceData.price.replace('/yr', '')}${savingsPriceData.currencySuffix ? ` ${savingsPriceData.currencySuffix}` : ''}`
    : null;

  const discountLabelText =
    showDiscount && discountPercentage != null
      ? (copy?.modal?.discountLabel || '{{percent}}% off first year').replace('{{percent}}', String(discountPercentage))
      : null;

  const discountStepText = showDiscount
    ? (copy?.modal?.steps?.discountApplied || '{{percent}}% early-adopter discount applied — first year {{price}}')
        .replace('{{percent}}', String(discountPercentage ?? ''))
        .replace('{{price}}', formattedDiscountedPrice || '') +
      (formattedSavings
        ? ` ${(copy?.modal?.steps?.saveSuffix || '(save {{savings}})').replace('{{savings}}', formattedSavings)}`
        : '')
    : null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!customerName.trim()) {
      newErrors.customerName = copy?.modal?.errors?.customerNameRequired || 'Customer name is required';
    }

    if (!billingAddress || !addressComplete) {
      newErrors.billingAddress = copy?.modal?.errors?.billingAddressRequired || 'Please complete the billing address';
    }

    if (organizationName && organizationName.length > 50) {
      newErrors.organizationName = copy?.modal?.errors?.organizationNameTooLong || 'Organization name must be 50 characters or fewer';
    }

    // Company name is required when purchasing as a business (VAT invoices must identify the company)
    if (isBusinessPurchase && !organizationName?.trim()) {
      newErrors.organizationName = copy?.modal?.errors?.organizationNameRequired || 'Company name is required for business purchases';
    }

    if (phone.trim()) {
      const digitsOnly = phone.replace(/\D/g, '');
      if (!/^[0-9+\-(). ]+$/.test(phone)) {
        newErrors.phone = copy?.modal?.errors?.phoneInvalid || 'Phone number contains invalid characters';
      } else if (digitsOnly.length < 7) {
        newErrors.phone = copy?.modal?.errors?.phoneTooShort || 'Phone number must have at least 7 digits';
      } else if (digitsOnly.length > 15) {
        newErrors.phone = copy?.modal?.errors?.phoneTooLong || 'Phone number must have 15 digits or fewer';
      }
    }

    if (poNumber.length > 50) {
      newErrors.poNumber = copy?.modal?.errors?.poNumberTooLong || 'PO number must be 50 characters or fewer';
    }

    // VAT ID is required when purchasing as a business in EU
    if (isBusinessPurchase && vatCollectionEnabled && !vatId.trim()) {
      newErrors.vatId = copy?.modal?.errors?.vatIdRequired || 'VAT ID is required for business purchases';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddressChange = (event: StripeAddressElementChangeEvent) => {
    setAddressComplete(event.complete);
    if (event.complete && event.value?.address) {
      const addr = event.value.address;
      setBillingAddress({
        line1: addr.line1 || '',
        line2: addr.line2 || undefined,
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.postal_code || '',
        country: addr.country || '',
      });
      // Capture organization name from address element (when display.name = 'organization')
      setOrganizationName(event.value.name || undefined);
    } else {
      setBillingAddress(null);
      setOrganizationName(undefined);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && billingAddress) {
      onConfirm({
        customerName: customerName.trim(),
        organizationName: organizationName || undefined,
        phone: phone.trim() || undefined,
        poNumber: poNumber.trim() || undefined,
        billingAddress,
        vatId: isBusinessPurchase && vatId.trim() ? vatId.trim().toUpperCase() : undefined,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-primary-50 -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
          <DialogTitle className="text-xl">
            {copy?.modal?.title || 'Request Invoice'}
            <small className="text-primary-100 text-sm block">
              {planName} Membership -{' '}
              {discountLoading ? (
                // Discount-eligible user: hold a placeholder until proration_preview
                // resolves so the price doesn't flash from full to discounted.
                <span
                  className="inline-block h-4 w-24 align-middle animate-pulse rounded bg-primary-100/70"
                  role="status"
                  aria-label={copy?.modal?.loadingPrice || 'Loading price'}
                />
              ) : showDiscount ? (
                <>
                  <span className="line-through opacity-70">{formattedOriginalPrice}</span>{' '}
                  <span className="font-semibold">{formattedDiscountedPrice}</span>
                  {discountLabelText && <span> · {discountLabelText}</span>}
                </>
              ) : (
                formattedPrice
              )}
            </small>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {/* Name and Phone in two columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Customer Name */}
            <div className="space-y-2">
              <Label htmlFor="customerName">
                {copy?.modal?.fields?.customerName || 'Your Name'} <span className="text-destructive">*</span>
              </Label>
              <Input id="customerName" value={customerName} onChange={(e) => setCustomerName(e.target.value)} disabled={loading} />
              {errors.customerName && <p className="text-sm text-destructive">{errors.customerName}</p>}
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-muted-foreground">
                {copy?.modal?.fields?.phone || 'Phone Number'} <span className="text-xs font-normal">({copy?.modal?.fields?.optional || 'Optional'})</span>
              </Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} disabled={loading} />
              {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
            </div>
          </div>

          {/* PO Number */}
          <div className="space-y-2">
            <Label htmlFor="poNumber" className="text-muted-foreground">
              {copy?.modal?.fields?.poNumber || 'PO Number'} <span className="text-xs font-normal">({copy?.modal?.fields?.optional || 'Optional'})</span>
            </Label>
            <Input id="poNumber" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} maxLength={50} disabled={loading} />
            {errors.poNumber && <p className="text-sm text-destructive">{errors.poNumber}</p>}
          </div>

          {/* Your Email (read-only) - Invoice will be sent here */}
          <div className="space-y-2">
            <Label htmlFor="userEmail" className="text-muted-foreground">
              {copy?.modal?.fields?.yourEmail || 'Your Email'} <span className="text-xs font-normal">({copy?.modal?.fields?.invoiceDestination || 'invoice will be sent here'})</span>
            </Label>
            <Input id="userEmail" value={userEmail} disabled className="bg-muted" />
          </div>

          {/* Billing Information */}
          <div className="space-y-2">
            <Label>
              {copy?.modal?.fields?.billingAddress || 'Billing Information'} <span className="text-destructive">*</span>
            </Label>

            {/* B2B / VAT section - grouped under Billing Information header */}
            {vatCollectionEnabled && (
              <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="businessPurchase"
                    checked={isBusinessPurchase}
                    onChange={(e) => setIsBusinessPurchase(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <Label htmlFor="businessPurchase" className="cursor-pointer">
                    {copy?.modal?.fields?.businessPurchase || 'Purchasing as a business?'}
                  </Label>
                </div>

                {isBusinessPurchase && (
                  <div className="space-y-2 ml-6">
                    <Label htmlFor="vatId">
                      {copy?.modal?.fields?.vatId || 'VAT ID'} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="vatId"
                      placeholder="e.g., DE123456789"
                      value={vatId}
                      onChange={(e) => setVatId(e.target.value.toUpperCase())}
                      disabled={loading}
                    />
                    <p className="text-xs text-muted-foreground">
                      {copy?.modal?.fields?.vatIdHint || 'Enter your VAT/Tax ID number'}
                    </p>
                    {(errors.vatId || vatError) && <p className="text-sm text-destructive">{vatError || errors.vatId}</p>}
                  </div>
                )}
              </div>
            )}

            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <AddressElement
                  options={{
                    mode: 'billing',
                    display: {
                      name: 'organization',
                    },
                    defaultValues: defaultOrganizationName ? {
                      name: defaultOrganizationName,
                    } : undefined,
                    autocomplete: googleMapsApiKey ? {
                      mode: 'google_maps_api',
                      apiKey: googleMapsApiKey,
                    } : undefined,
                  }}
                  onChange={handleAddressChange}
                />
              </Elements>
            ) : (
              <p className="text-sm text-muted-foreground">Loading address form...</p>
            )}
            {errors.organizationName && <p className="text-sm text-destructive">{errors.organizationName}</p>}
            {errors.billingAddress && <p className="text-sm text-destructive">{errors.billingAddress}</p>}
          </div>

          {/* What happens next */}
          <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 text-sm space-y-2">
            <p className="font-medium text-primary-800">{copy?.modal?.whatHappensNext || 'What happens next:'}</p>
            <ul className="space-y-1 text-primary-700">
              {discountStepText && (
                <li className="flex items-start gap-2 font-semibold">
                  <Check className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{discountStepText}</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{copy?.modal?.steps?.step1 || 'By paying through invoice your membership activates immediately. There is no trial period.'}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{copy?.modal?.steps?.step2 || 'Invoice sent to your email with bank transfer details'}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{copy?.modal?.steps?.step4?.replace('{{days}}', String(daysUntilDue)) || `Payment due within ${daysUntilDue} days`}</span>
              </li>
            </ul>
          </div>

          {serverError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
              {serverError}
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button type="submit" variant="default" disabled={loading} className="w-full">
              {loading ? copy?.modal?.buttons?.processing || 'Processing...' : copy?.modal?.buttons?.confirm || 'Activate Membership & Send Invoice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
};

export default InvoiceRequestModal;
